/**
 * MXlab — Options.
 *
 * Ajout au panier des accessoires. Deux mécanismes coexistent, à dessein :
 *
 * 1. Ajout au panier standard (bouton « Ajouter au panier ») → on INTERCEPTE la
 *    soumission et on poste le format multi-articles natif `items: [{ id, quantity,
 *    properties }, …]` à /cart/add.js, en un seul POST (kit + accessoires). Voir
 *    `handleSubmit` / `addToCart`. C'est le vrai chemin qui crée une ligne de
 *    panier par accessoire.
 *
 * 2. Inputs `shopify-additional-items[i][...]` rattachés au form via l'attribut
 *    HTML `form` → conservés car ils alimentent l'ACCELERATED CHECKOUT (Shop Pay
 *    « Acheter maintenant » & boutons de paiement dynamiques). ATTENTION : ces
 *    champs sont IGNORÉS par /cart/add.js ; ils ne créent PAS de ligne de panier.
 *    Ne pas s'y fier pour le bouton « Ajouter au panier » (piège d'origine).
 *    Les index sont renumérotés à partir de 1 (Shopify l'exige), sans trou.
 *
 * Le total est calculé ici et non en Liquid : le prix du kit vient de la variante
 * (événement de changement de variante, cf. bloc ci-dessous), les
 * accessoires de leurs propres variantes.
 * Le panier reste la seule source de vérité — ce total est un affichage.
 */
/**
 * EVENEMENTS DE PANIER ET DE VARIANTE (@shopify/events), dans les deux sens.
 *
 * ECOUTE — `shopify:product:select`. L'EVENEMENT EST EMIS AVANT LE FETCH : au moment ou
 * il arrive, le nouveau prix n'existe pas encore. Il est lu dans
 * `(await event.promise).detail.resource.price`, et `normalizeVariantEvent` attend cette
 * promesse pour rendre { resource, data }.
 *
 * EMISSION — `shopify:cart:lines-update`. L'evenement est construit A LA MAIN plutot
 * qu'en important `CartLinesUpdateEvent` de @shopify/events : ce module n'est accessible
 * que par l'import map de `snippets/scripts.liquid`, un fichier natif qu'on ne forke pas.
 * C'est sur — aucun `instanceof` n'est fait sur ces evenements dans tout le 4.1.3, les
 * consommateurs lisent `event.promise` et `event.detail` (cart-icon.js:58,
 * component-cart-items.js:61) : un Event nu portant les memes proprietes est
 * indiscernable. Repli natif si notre `detail.sections` n'est pas exploitable :
 * component-cart-items.js:70 refait un `sectionRenderer.renderSection()` de lui-meme.
 */
const MODERN_VARIANT_EVENT = 'shopify:product:select';
const MODERN_CART_EVENT = 'shopify:cart:lines-update';

/**
 * Attend la promesse de l'evenement de variante, puis passe { resource, data } a `apply`.
 *
 * @param {Event & { promise?: Promise<any> }} event
 * @param {(payload: { resource: any, data: any }) => void} apply
 */
function normalizeVariantEvent(event, apply) {
  event.promise
    .then((result) => {
      const detail = result?.detail;
      if (!detail) return;
      apply({ resource: detail.resource ?? result?.variant ?? null, data: detail });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[mx-options] promesse de variante rejetee :', error);
    });
}

/**
 * Reproduit le filtre `money` de Liquid a partir de shop.money_format.
 * Volontairement duplique plutot qu'importe : `@theme/*` est un import map
 * explicite dans snippets/scripts.liquid. Y ajouter une entree obligerait a
 * modifier un fichier du theme — et un import non resolu tue tout le module.
 *
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = cents / 100;

  const group = (num, thousands, decimal, decimals) => {
    const fixed = num.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const spaced = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? `${spaced}${decimal}${frac}` : spaced;
  };

  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount':
        return group(value, ',', '.', 2);
      case 'amount_no_decimals':
        return group(value, ',', '.', 0);
      case 'amount_with_comma_separator':
        return group(value, '.', ',', 2);
      case 'amount_no_decimals_with_comma_separator':
        return group(value, '.', ',', 0);
      case 'amount_with_space_separator':
        return group(value, ' ', ',', 2);
      case 'amount_no_decimals_with_space_separator':
        return group(value, ' ', ',', 0);
      case 'amount_with_apostrophe_separator':
        return group(value, "'", '.', 2);
      default:
        return group(value, ',', '.', 2);
    }
  });
}

class MxOptions extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    // NE PAS nommer ceci `this.hidden` : `hidden` est une propriete native de
    // HTMLElement (un booleen reflete). L'assignation ecraserait l'attribut
    // hidden de l'element au lieu de stocker le noeud, et toute lecture
    // renverrait `true` au lieu du conteneur.
    this.hiddenFields = this.querySelector('[data-mx-hidden]');
    this.rows = [...this.querySelectorAll('[data-mx-row]')];

    // La carte est un <label> : cliquer coche/décoche l'accessoire. Le clic sur le
    // sélecteur de variante ne doit PAS basculer la carte. Le <label> l'empêche déjà
    // nativement (un select est interactif), mais on ajoute un stopPropagation de
    // sécurité. Le `change` du select remonte quand même → sync() met à jour la
    // variante (prix/image), sans toucher à l'état coché.
    for (const select of this.querySelectorAll('[data-mx-variant]')) {
      select.addEventListener('click', (event) => event.stopPropagation());
    }

    this.totalEl = this.querySelector('[data-mx-total]');
    this.countEl = this.querySelector('[data-mx-count]');

    this.kitPrice = parseInt(this.dataset.kitPrice, 10) || 0;

    // On formate comme la boutique, pas comme Intl : sinon le total en JS
    // et les prix en Liquid n'ont pas la meme tete sur la meme page.
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.productId = this.dataset.productId;

    this.addEventListener('change', () => this.sync());

    // Le prix du kit change quand le client change de matière.
    // Charge utile normalisée entre 2.1.5 et 4.1.3 (cf. bloc COMPATIBILITÉ en tête).
    this.applyVariant = ({ resource, data }) => {
      if (!resource || typeof resource.price !== 'number') return;
      // Une autre fiche produit sur la page ne doit pas nous impacter.
      if (data?.productId && String(data.productId) !== String(this.productId)) return;
      this.kitPrice = resource.price;
      this.updateTotal();
    };

    // GARDE DE SIGNATURE, à ne pas confondre avec l'ancien verrou de famille (retiré le
    // 05/08/2026 avec la double écoute) : elle protège d'un `shopify:product:select` émis
    // par une app tierce SANS promesse, sur lequel `.then()` lèverait un TypeError.
    this.onVariant = (event) => {
      if (typeof event?.promise?.then !== 'function') return;
      normalizeVariantEvent(event, this.applyVariant);
    };
    document.addEventListener(MODERN_VARIANT_EVENT, this.onVariant);

    // Interception de l'ajout au panier.
    //
    // Les inputs `shopify-additional-items` ne sont PAS lus par /cart/add.js :
    // c'est un mécanisme d'accelerated checkout (Shop Pay), pas d'ajout au panier.
    // Pour créer une ligne de panier par accessoire, il faut poster le format
    // multi-articles natif `items: [{ id, quantity, properties }, …]`.
    //
    // On court-circuite donc le `handleSubmit` natif : ce dernier est un listener
    // délégué posé sur `document` en phase CAPTURE (component.js). Un listener sur
    // `window` en capture s'exécute AVANT lui (window précède document dans la
    // descente de capture) ; `stopImmediatePropagation()` l'empêche alors de tourner.
    //
    // INVARIANT PANIER (ordre des intercepteurs) — NE PAS CASSER : mx-validate est rendu
    // AVANT ce bloc (dans la section, avant la grille) → son listener window/capture est posé
    // en 1er et peut bloquer celui-ci par stopImmediatePropagation quand le formulaire est
    // invalide. NE PAS enregistrer cet écouteur avant mx-validate, NE PAS déplacer mx-validate
    // après la grille. `handleSubmit` lit la FormData de façon synchrone : NE PAS le rendre
    // async avant cette lecture (sinon les champs désactivés par mx-config/mx-note au clic
    // seraient déjà réactivés → propriétés vides au panier).
    this.onSubmit = (event) => this.handleSubmit(event);
    window.addEventListener('submit', this.onSubmit, { capture: true });

    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(MODERN_VARIANT_EVENT, this.onVariant);
    window.removeEventListener('submit', this.onSubmit, { capture: true });
  }

  /**
   * Intercepte la soumission du formulaire panier quand des accessoires sont
   * cochés, et envoie un unique POST `items[]` (kit + accessoires) à /cart/add.js.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    const form = event.target;
    // Pas notre formulaire panier : on ne touche à rien.
    // NB : `form.id` est CLOBBERÉ par l'input `name="id"` (l'ID de variante) — une
    // propriété nommée du form écrase la propriété `id` native. On lit donc
    // l'attribut directement, comme le fait product-form.js (form.getAttribute('id')).
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.formId) return;

    // Accessoires cochés (mêmes règles que sync/updateTotal).
    const accessories = this.rows.map((row) => this.selected(row)).filter(Boolean);
    // Kit seul : on laisse le flux natif faire son travail habituel (qui gère aussi
    // un éventuel fichier, envoyé en multipart par le natif).
    if (accessories.length === 0) return;

    // À partir d'ici, on prend la main : le handleSubmit natif ne doit pas tourner.
    event.preventDefault();
    event.stopImmediatePropagation();

    // Un fichier (line item property) impose le MULTIPART, incompatible avec un POST
    // JSON `items[]` (un File se sérialise en `{}`). Dans ce cas on scinde en 2 POST :
    // kit+fichier en multipart, accessoires en JSON. Sinon, le chemin JSON habituel.
    const fd = new FormData(form);
    const hasFile = [...fd.values()].some((value) => value instanceof File && value.size > 0);

    if (hasFile) {
      this.addToCartWithFile(form, fd, accessories);
    } else {
      this.addToCart(form, accessories);
    }
  }

  /**
   * Envoi au panier SANS fichier : un seul POST JSON `items[]` (kit + accessoires).
   * Le kit est reconstruit depuis la FormData (préserve les line item properties de
   * mx-bike / mx-config) et envoyé EN DERNIER (→ /cart/add.js l'inverse → kit en tête).
   *
   * @param {HTMLFormElement} form
   * @param {Array<{id: string, price: number, title: string}>} accessories
   */
  addToCart(form, accessories) {
    const fd = new FormData(form);

    const kit = { id: fd.get('id'), quantity: Number(fd.get('quantity')) || 1 };
    const sellingPlan = fd.get('selling_plan');
    if (sellingPlan) kit.selling_plan = sellingPlan;

    const kitProps = {};
    for (const [name, value] of fd.entries()) {
      // Vraies line item properties du kit uniquement ; on ignore shopify-additional-items,
      // form_type / utf8 / sections.
      const match = name.match(/^properties\[(.+)\]$/);
      if (match) kitProps[match[1]] = value;
    }
    if (Object.keys(kitProps).length) kit.properties = kitProps;

    // Accessoires d'abord, kit EN DERNIER → inversion /cart/add.js → kit en tête.
    const items = this.accessoryItems(accessories);
    items.push(kit);

    this.setLoading(true);
    fetch(this.cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: this.cartSections() }),
    })
      .then((response) => response.json())
      .then((response) => {
        this.setLoading(false);
        if (response.status) {
          this.showCartError(this.dataset.errorGeneric, response);
          return;
        }
        this.clearCartError();
        const totalQty = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        this.refreshCart(response.sections, totalQty);
      })
      .catch((error) => {
        this.setLoading(false);
        this.showCartError(this.dataset.errorGeneric);
        console.error('[mx-options] Échec de l’ajout au panier :', error);
      });
  }

  /**
   * Chemin FICHIER : 2 POST SÉQUENTIELS, dans cet ordre pour l'ATOMICITÉ —
   *   POST 1 : KIT + fichiers en MULTIPART (le plus lourd/risqué ; seul format que
   *            /cart/add.js accepte pour un upload). S'il échoue → on N'ENVOIE PAS
   *            les accessoires (rien de partiel au panier) + erreur.
   *   POST 2 : accessoires en JSON `items[]`. S'il échoue → le kit RESTE au panier,
   *            on informe que les accessoires n'ont pas été ajoutés.
   * Les `shopify-additional-items` de la FormData sont ignorés par /cart/add.js
   * (accelerated checkout), donc aucun doublon d'accessoire.
   *
   * @param {HTMLFormElement} form
   * @param {FormData} fd - FormData(form), déjà construite dans handleSubmit.
   * @param {Array<{id: string, price: number, title: string}>} accessories
   */
  addToCartWithFile(form, fd, accessories) {
    const url = this.cartAddUrl();
    const sections = this.cartSections();
    const items = this.accessoryItems(accessories);
    if (sections) fd.append('sections', sections);

    this.setLoading(true);

    // POST 1 — kit + fichiers (multipart : fetch pose seul le Content-Type).
    fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: fd })
      .then((response) => response.json())
      .then((kitResponse) => {
        if (kitResponse.status) {
          // Cas 1 : kit refusé → rien au panier, pas d'accessoires.
          this.setLoading(false);
          this.showCartError(this.dataset.errorKit, kitResponse);
          return undefined;
        }
        // POST 2 — accessoires (JSON).
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections }),
        })
          .then((response) => response.json())
          .then((accResponse) => {
            this.setLoading(false);
            if (accResponse.status) {
              // Cas 2 : kit OK mais accessoires refusés → le kit reste, on informe.
              this.refreshCart(kitResponse.sections, 1);
              this.showCartError(this.dataset.errorAcc, accResponse);
              return;
            }
            this.clearCartError();
            this.refreshCart(accResponse.sections, 1 + accessories.length);
          });
      })
      .catch((error) => {
        this.setLoading(false);
        this.showCartError(this.dataset.errorGeneric);
        console.error('[mx-options] Échec de l’ajout au panier (fichier) :', error);
      });
  }

  /** Items JSON des accessoires (propriété « Kit associé » si demandée par le réglage). */
  accessoryItems(accessories) {
    const stampKit = this.dataset.stampKit === 'true';
    const kitProperty = this.dataset.kitProperty || 'Kit associé';
    return accessories.map((accessory) => {
      const item = { id: accessory.id, quantity: 1 };
      if (stampKit) item.properties = { [kitProperty]: this.dataset.kitTitle };
      return item;
    });
  }

  /** URL de /cart/add.js (Theme.routes, sinon Shopify.routes, sinon défaut). */
  cartAddUrl() {
    return (
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) ||
      (window.Shopify && Shopify.routes && Shopify.routes.root + 'cart/add.js') ||
      '/cart/add.js'
    );
  }

  /** Sections à re-rendre = tous les <cart-items-component> de la page. */
  cartSections() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  /**
   * Redéclenche le mécanisme natif de rafraîchissement (tiroir + compteur).
   *
   * LE COMPTEUR D'EN-TÊTE VEUT UN TOTAL ABSOLU, ET C'EST TOUTE LA RAISON DU GET.
   * `cart.totalQuantity` est le total du panier (cart-icon.js:58), pas un delta à
   * additionner. Or `/cart/add.js` ne renvoie pas ce total : il est donc lu par un GET
   * `/cart.js` qui RÉSOUT la promesse de l'événement. C'est le motif natif —
   * `product-form.js:442` émet lui aussi avant d'avoir le résultat et résout ensuite.
   * ⚠️ Ce GET n'est PAS un reste de compatibilité : le retirer casserait le compteur.
   *
   * @param {Record<string, string>|undefined} sections - HTML des sections à morpher
   * @param {number} addedQty - nombre d'articles AJOUTÉS (delta), pas le total du panier
   */
  refreshCart(sections, addedQty) {
    // Construit à la main : @shopify/events n'est accessible que par l'import map d'un
    // fichier natif, et aucun consommateur ne fait d'`instanceof` (cf. bloc en tête).
    const detail = {
      // Fait sauter le rafraîchissement redondant du product-form natif (product-form.js:310),
      // exactement comme `data.source` le fait en 2.1.5.
      source: 'product-form-component',
      sourceId: 'mx-options',
      itemCount: addedQty,
      productId: this.productId,
      sections,
    };

    const modern = new Event(MODERN_CART_EVENT, { bubbles: true });
    modern.action = 'add'; // lu par cart-drawer.js:69 (ouverture auto)
    modern.context = 'product';
    modern.lines = [];
    modern.detail = detail;
    modern.promise = fetch(`${this.cartUrlRoot()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => ({
        cart: { totalQuantity: cart?.item_count ?? 0 },
        detail,
      }))
      // Panier illisible : on résout quand même. Les consommateurs retombent alors sur
      // `detail.itemCount` (compteur) et sur `sectionRenderer.renderSection()` pour le
      // rendu (component-cart-items.js:70) — dégradé, jamais bloqué.
      .catch(() => ({ cart: null, detail }));

    document.dispatchEvent(modern);
  }

  /** Racine des routes panier (préfixe de langue compris), avec repli. */
  cartUrlRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /** Boutons submit rattachés à NOTRE formulaire (bouton principal + barre mobile). */
  buyButtons() {
    return [...document.querySelectorAll('button[type="submit"], input[type="submit"]')].filter(
      (b) => (b.getAttribute('form') || (b.form && b.form.getAttribute('id'))) === this.formId
    );
  }

  /**
   * État de chargement des boutons d'ajout : DÉSACTIVÉS (anti double-commande) +
   * libellé de chargement (via la classe `.mx-cart-busy`, cf. stylesheet du bloc).
   */
  setLoading(on) {
    for (const btn of this.buyButtons()) {
      btn.disabled = on;
      btn.classList.toggle('mx-cart-busy', on);
      if (on) {
        btn.setAttribute('aria-busy', 'true');
        btn.dataset.mxLoading = this.dataset.loadingLabel || 'Ajout en cours…';
      } else {
        btn.removeAttribute('aria-busy');
        delete btn.dataset.mxLoading;
      }
    }
  }

  /** Affiche une erreur visible sous le bouton d'ajout (détail technique en console). */
  showCartError(message, detail) {
    const btn = this.buyButtons()[0];
    if (!btn) {
      console.error('[mx-options]', message, detail || '');
      return;
    }
    const anchor = btn.closest('add-to-cart-component') || btn;
    let el = document.querySelector('[data-mx-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-mx-cart-error', '');
      el.className = 'mx-cart-error';
      el.setAttribute('role', 'alert');
      anchor.insertAdjacentElement('afterend', el);
    }
    el.textContent = message || 'L’ajout au panier a échoué.';
    el.hidden = false;
    if (detail) console.error('[mx-options]', message, detail.description || detail.message || detail);
  }

  clearCartError() {
    const el = document.querySelector('[data-mx-cart-error]');
    if (el) el.hidden = true;
  }

  /** Variante retenue pour une ligne : celle du select s'il existe, sinon l'unique. */
  selected(row) {
    const check = row.querySelector('[data-mx-check]');
    if (!check?.checked) return null;

    const name = row.querySelector('[data-mx-name]')?.textContent.trim() ?? '';
    const select = row.querySelector('[data-mx-variant]');

    if (select) {
      const opt = select.options[select.selectedIndex];
      const suffix = opt?.textContent.trim();
      return {
        id: select.value,
        price: parseInt(opt?.dataset.price, 10) || 0,
        title: suffix ? `${name} — ${suffix}` : name,
      };
    }

    return {
      id: check.value,
      price: parseInt(check.dataset.price, 10) || 0,
      title: name,
    };
  }

  sync() {
    // Le prix ET l'image de chaque ligne suivent le select, coché ou non.
    for (const row of this.rows) {
      const select = row.querySelector('[data-mx-variant]');
      if (!select) continue;

      const option = select.options[select.selectedIndex];

      const priceEl = row.querySelector('[data-mx-price]');
      if (priceEl) {
        priceEl.textContent = `+${formatMoney(parseInt(option?.dataset.price, 10) || 0, this.moneyFormat)}`;
      }

      const img = row.querySelector('[data-mx-img]');
      const src = option?.dataset.image;
      if (img && src && img.src !== src) img.src = src;
    }

    // On reconstruit les inputs à chaque fois : pas d'état résiduel possible.
    this.hiddenFields.replaceChildren();

    let index = 1;
    for (const row of this.rows) {
      const variant = this.selected(row);
      if (!variant) continue;

      this.field(`shopify-additional-items[${index}][id]`, variant.id);
      this.field(`shopify-additional-items[${index}][quantity]`, '1');

      if (this.dataset.stampKit === 'true') {
        const key = this.dataset.kitProperty || 'Kit associé';
        this.field(`shopify-additional-items[${index}][properties][${key}]`, this.dataset.kitTitle);
      }

      index += 1;
    }

    this.updateTotal();
  }

  field(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('form', this.formId);
    this.hiddenFields.appendChild(input);
  }

  updateTotal() {
    let total = this.kitPrice;
    const items = [];

    for (const row of this.rows) {
      const variant = this.selected(row);
      if (!variant) continue;
      total += variant.price;
      items.push({ title: variant.title, price: variant.price });
    }

    // Le total interne du bloc reste dispo, mais dans le layout configurateur
    // c'est `mx-summary` (panneau sticky) qui porte le total. On le previent.
    if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat);

    if (this.countEl) {
      this.countEl.textContent = items.length
        ? `— kit + ${items.length} accessoire${items.length > 1 ? 's' : ''}`
        : '';
    }

    document.dispatchEvent(
      new CustomEvent('mx:options', {
        bubbles: true,
        detail: { productId: this.productId, items },
      })
    );
  }
}

if (!customElements.get('mx-options')) {
  customElements.define('mx-options', MxOptions);
}